import { MetricsItem } from 'components/segments';
import { CardBackground } from 'components/ui';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetCustomerStatisticsQuery } from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';

export default function Overview() {
  const {
    output: { filtersApiPayload },
  } = useAppSelector(composableFiltersState);
  const { currentStore } = useAppSelector(userDetailsState);

  const currency = currentStore.completeInfo?.currency;
  const locale = currentStore.completeInfo?.primary_locale ?? 'en';

  const {
    data: metrics,
    isFetching: loading,
    isError,
  } = useGetCustomerStatisticsQuery(
    {
      body: filtersApiPayload,
      params: {
        metric_list: ['aov', 'aclv', 'arpu', 'ano', 'num_users', 'num_orders'],
        store_name: String(currentStore.completeInfo?.store_name),
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );

  const {
    data: metricsV2,
    isFetching: loadingV2,
    isError: errorV2,
  } = useGetCustomerStatisticsQuery(
    {
      body: filtersApiPayload,
      params: {
        metric_list: ['acr', 'aor'],
        store_name: String(currentStore.completeInfo?.store_name),
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );
  const {
    data: grossMarginData,
    isFetching: grossMarginLoading,
    isError: grossMarginError,
  } = useGetCustomerStatisticsQuery(
    {
      body: filtersApiPayload,
      params: {
        metric_list: ['gross_margin'],
        store_name: String(currentStore.completeInfo?.store_name),
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );

  if (isError && errorV2 && grossMarginError) return null;

  const users = (
    (metrics?.num_users?.length && metrics?.num_users[0].value) ||
    '--'
  ).toLocaleString(locale);
  const aov = (
    (metrics?.aov?.length && metrics.aov[0].value) ||
    '--'
  ).toLocaleString(locale, {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const arpu = (
    (metrics?.arpu?.length && metrics.arpu[0].value) ||
    '--'
  ).toLocaleString(locale, {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const aclv = (
    (metrics?.aclv?.length && metrics.aclv[0].value) ||
    '--'
  ).toLocaleString(locale, {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const ano =
    (metrics?.ano?.length && metrics.ano[0].value?.toFixed(2)) || '--';

  const aor = (
    (metricsV2?.aor?.length && metricsV2.aor[0].value) ||
    '--'
  ).toLocaleString(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
  const acr = (
    (metricsV2?.acr?.length && metricsV2.acr[0].value) ||
    '--'
  ).toLocaleString(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
  const grossMargin = (
    (grossMarginData?.gross_margin?.length &&
      grossMarginData?.gross_margin[0].value) ||
    '--'
  ).toLocaleString(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
  return (
    <section className="mt-7 md:mt-16">
      <p className="text-base tracking-wide md:text-2xl ">Overview</p>
      <div className="mt-2 -mx-segments-10px md:mx-0 md:mt-6">
        <CardBackground>
          <div className="grid grid-cols-2 px-5 py-2 divide-y xl:grid-cols-4 lg:px-10">
            <MetricsItem
              loading={loading}
              label="Users"
              amount={users}
              tooltip="Total users in segment"
            />
            <div className="border-none lg:pl-4">
              <MetricsItem
                loading={loading}
                label="AOV"
                amount={aov}
                tooltip="Avg. order value"
              />
            </div>
            <div className="xl:border-none lg:pl-4">
              <MetricsItem
                loading={loading}
                label="ARPU"
                amount={arpu}
                tooltip="Avg. revenue per user"
              />
            </div>
            <div className="xl:border-none lg:pl-4">
              <MetricsItem
                loading={loading}
                label="Frequency"
                amount={ano}
                tooltip="Avg. orders per user"
              />
            </div>

            <MetricsItem
              loading={loading}
              label="ACLV"
              amount={aclv}
              tooltip="Avg. customer lifetime value"
            />
            <div className="lg:pl-4">
              <MetricsItem
                loading={loadingV2}
                label="AOR"
                amount={aor}
                tooltip="Avg. emails opened / received"
              />
            </div>
            <div className="lg:pl-4">
              <MetricsItem
                loading={loadingV2}
                label="ACR"
                amount={acr}
                tooltip="Avg. emails clicked / opened"
              />
            </div>
            <div className="lg:pl-4">
              <MetricsItem
                loading={grossMarginLoading}
                label="Gross margin"
                amount={grossMargin}
                tooltip="Gross profit / Net revenue"
              />
            </div>
          </div>
        </CardBackground>
      </div>
    </section>
  );
}
